import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Download, Search } from 'lucide-react';
import api from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Switch } from '../../components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';

const NONE = '__all__';
const DOC_STATUSES = ['NOT_REQUIRED', 'REQUESTED', 'TO_ISSUE', 'ISSUED', 'CANCELLED'];
const eur = (v) => (typeof v === 'number' ? `€ ${v.toFixed(2)}` : '—');
const fmtDt = (d) => (d ? new Date(d).toLocaleString('it-IT', { day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit' }) : '—');

function SettingsCard() {
  const [settings, setSettings] = useState(null);
  useEffect(() => { api.get('/admin/settings').then(({ data }) => setSettings(data.settings)).catch(() => {}); }, []);
  if (!settings) return null;

  const save = async () => {
    const payload = {
      max_devices: Number(settings.max_devices),
      revoke_on_refund: !!settings.revoke_on_refund,
      default_vat_rate: settings.default_vat_rate === '' || settings.default_vat_rate === null ? null : Number(settings.default_vat_rate),
      company: settings.company,
      receipt_note: settings.receipt_note,
    };
    await api.put('/admin/settings', payload);
    toast.success('Impostazioni salvate');
  };

  return (
    <div className="bg-white border border-slate-200 rounded-2xl p-6 mb-8" data-testid="fiscal-settings">
      <h3 className="font-display font-semibold text-slate-900 mb-1">Configurazione piattaforma</h3>
      <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 mb-5">
        Le impostazioni fiscali sono <strong>DA VERIFICARE PRIMA DEL GO-LIVE</strong> con il commercialista. Nessuna aliquota viene applicata automaticamente dal sistema.
      </p>
      <div className="grid sm:grid-cols-3 gap-4 mb-4">
        <div>
          <Label>Max dispositivi per account</Label>
          <Input type="number" min={1} max={10} className="mt-1.5" data-testid="set-max-devices"
            value={settings.max_devices} onChange={(e) => setSettings({ ...settings, max_devices: e.target.value })} />
        </div>
        <div>
          <Label>Aliquota IVA predefinita (%)</Label>
          <Input className="mt-1.5" placeholder="Non impostata — DA VERIFICARE" data-testid="set-vat-rate"
            value={settings.default_vat_rate ?? ''} onChange={(e) => setSettings({ ...settings, default_vat_rate: e.target.value })} />
        </div>
        <div className="flex items-end gap-2 pb-2">
          <Switch checked={!!settings.revoke_on_refund}
            onCheckedChange={(v) => setSettings({ ...settings, revoke_on_refund: v })} data-testid="set-revoke-refund" />
          <Label className="text-sm">Revoca accesso al rimborso</Label>
        </div>
      </div>
      <div className="grid sm:grid-cols-2 gap-4">
        {[['name', 'Nome azienda'], ['vat_number', 'P.IVA'], ['address', 'Indirizzo sede'], ['pec', 'PEC aziendale'], ['codice_destinatario', 'Codice destinatario']].map(([k, l]) => (
          <div key={k}>
            <Label>{l}</Label>
            <Input className="mt-1.5" value={settings.company[k] || ''}
              onChange={(e) => setSettings({ ...settings, company: { ...settings.company, [k]: e.target.value } })} />
          </div>
        ))}
      </div>
      <Button onClick={save} data-testid="settings-save" className="mt-5 rounded-full bg-brand hover:bg-brand-hover text-white px-6">Salva configurazione</Button>
    </div>
  );
}

export default function AdminSales() {
  const [items, setItems] = useState([]);
  const [filters, setFilters] = useState({ q: '', tipo: NONE, payment_status: NONE, document_status: NONE });
  const [detail, setDetail] = useState(null);

  const qs = () => {
    const p = new URLSearchParams();
    Object.entries(filters).forEach(([k, v]) => { if (v && v !== NONE) p.set(k, v); });
    return p.toString();
  };

  const load = () => api.get(`/admin/sales?${qs()}`).then(({ data }) => setItems(data.items)).catch(() => {});
  // Search text is applied explicitly; select filters reload automatically.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [filters.tipo, filters.payment_status, filters.document_status]);

  const exportCsv = async () => {
    const { data } = await api.get(`/admin/sales/export.csv?${qs()}`, { responseType: 'blob' });
    const url = URL.createObjectURL(data);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'vendite_export.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  const openDetail = async (orderId) => {
    const { data } = await api.get(`/admin/sales/${orderId}`);
    setDetail(data.order);
  };

  const setDocStatus = async (orderId, status) => {
    await api.patch(`/admin/sales/${orderId}/document`, { document_status: status });
    toast.success(`Stato documento: ${status}`);
    setDetail((d) => ({ ...d, document_status: status }));
    load();
  };

  const F = (k) => ({ value: filters[k], onValueChange: (v) => setFilters({ ...filters, [k]: v }) });

  return (
    <div data-testid="admin-sales">
      <h2 className="font-display font-semibold text-lg text-slate-900 mb-6">Vendite e fiscalità</h2>
      <SettingsCard />

      <div className="flex flex-wrap gap-3 mb-5 items-end">
        <div className="relative flex-1 min-w-[220px]">
          <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400" size={15} strokeWidth={1.5} />
          <Input placeholder="Cerca cliente, concorso, ordine..." className="pl-10 h-11" data-testid="sales-search"
            value={filters.q} onChange={(e) => setFilters({ ...filters, q: e.target.value })}
            onKeyDown={(e) => e.key === 'Enter' && load()} />
        </div>
        <Select {...F('tipo')}><SelectTrigger className="w-36 h-11" data-testid="sales-tipo"><SelectValue placeholder="Tipo" /></SelectTrigger>
          <SelectContent><SelectItem value={NONE}>B2C + B2B</SelectItem><SelectItem value="B2C">B2C</SelectItem><SelectItem value="B2B">B2B</SelectItem></SelectContent></Select>
        <Select {...F('payment_status')}><SelectTrigger className="w-36 h-11" data-testid="sales-pay"><SelectValue placeholder="Pagamento" /></SelectTrigger>
          <SelectContent><SelectItem value={NONE}>Tutti</SelectItem><SelectItem value="paid">paid</SelectItem><SelectItem value="refunded">refunded</SelectItem></SelectContent></Select>
        <Select {...F('document_status')}><SelectTrigger className="w-44 h-11" data-testid="sales-doc"><SelectValue placeholder="Documento" /></SelectTrigger>
          <SelectContent><SelectItem value={NONE}>Tutti</SelectItem>{DOC_STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent></Select>
        <Button onClick={load} variant="outline" className="h-11 rounded-xl" data-testid="sales-apply">Applica</Button>
        <Button onClick={exportCsv} className="h-11 rounded-xl bg-brand hover:bg-brand-hover text-white" data-testid="sales-export">
          <Download size={15} strokeWidth={1.5} className="mr-1.5" /> Export CSV (commercialista)
        </Button>
      </div>

      <div className="bg-white border border-slate-200 rounded-2xl overflow-x-auto">
        <table className="w-full text-sm min-w-[960px]">
          <thead>
            <tr className="border-b border-slate-200 text-left">
              {['Data', 'Cliente', 'Paese', 'Tipo', 'Concorso', 'Netto', 'Imposta', 'Totale', 'Aliquota', 'Pagamento', 'Rimborso', 'Documento', ''].map((h) => (
                <th key={h} className="px-4 py-3 text-xs tracking-[0.1em] uppercase text-slate-500 font-semibold">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {items.map((o) => (
              <tr key={o.order_id} data-testid={`sale-row-${o.order_id}`}>
                <td className="px-4 py-3 text-slate-500 whitespace-nowrap">{fmtDt(o.created_at)}</td>
                <td className="px-4 py-3 text-slate-700">{o.user_email || '—'}</td>
                <td className="px-4 py-3 text-slate-600">{o.customer_country || '—'}</td>
                <td className="px-4 py-3"><Badge className={o.customer_type === 'B2B' ? 'bg-violet-50 text-violet-700 border-violet-200 border' : 'bg-slate-100 text-slate-600'}>{o.customer_type}</Badge></td>
                <td className="px-4 py-3 text-slate-700 max-w-[180px] truncate">{o.contest_title || '—'}</td>
                <td className="px-4 py-3">{eur(o.amount_net)}</td>
                <td className="px-4 py-3">{eur(o.tax_amount)}</td>
                <td className="px-4 py-3 font-semibold">{eur(o.amount_gross)}</td>
                <td className="px-4 py-3">{o.tax_rate_applied !== null && o.tax_rate_applied !== undefined ? `${o.tax_rate_applied}%` : <span className="text-amber-600 text-xs">DA VERIFICARE</span>}</td>
                <td className="px-4 py-3"><Badge className={o.payment_status === 'paid' ? 'bg-emerald-50 text-emerald-700 border-emerald-200 border' : 'bg-slate-100 text-slate-500'}>{o.payment_status}</Badge></td>
                <td className="px-4 py-3 text-slate-600">{o.refund_status}</td>
                <td className="px-4 py-3 text-slate-600">{o.document_status}</td>
                <td className="px-4 py-3"><button onClick={() => openDetail(o.order_id)} className="text-brand text-xs font-semibold hover:underline" data-testid={`sale-detail-${o.order_id}`}>Dettaglio</button></td>
              </tr>
            ))}
          </tbody>
        </table>
        {items.length === 0 && <p className="text-sm text-slate-500 p-6">Nessun ordine con questi filtri.</p>}
      </div>

      <Dialog open={!!detail} onOpenChange={() => setDetail(null)}>
        <DialogContent className="max-w-xl max-h-[85vh] overflow-y-auto">
          {detail && (
            <>
              <DialogHeader><DialogTitle className="font-display">Ordine {detail.order_id}</DialogTitle></DialogHeader>
              <div className="grid grid-cols-2 gap-x-6 gap-y-2 text-sm mt-2" data-testid="sale-detail-panel">
                {[
                  ['Cliente', `${detail.user_name || ''} (${detail.user_email})`],
                  ['Concorso', detail.contest_title],
                  ['Tipo cliente', detail.customer_type],
                  ['Paese cliente', detail.customer_country || '—'],
                  ['Netto', eur(detail.amount_net)],
                  ['Imposta', eur(detail.tax_amount)],
                  ['Lordo', eur(detail.amount_gross) + ' ' + (detail.currency || '').toUpperCase()],
                  ['Aliquota', detail.tax_rate_applied ?? 'DA VERIFICARE'],
                  ['Tax jurisdiction', detail.tax_jurisdiction || 'DA VERIFICARE'],
                  ['Checkout Session', detail.stripe_session_id],
                  ['Payment Intent', detail.stripe_payment_intent_id || '—'],
                  ['Codice promo', detail.promo_code || '—'],
                  ['Pagamento', detail.payment_status],
                  ['Rimborso', detail.refund_status + (detail.refunded_at ? ` (${fmtDt(detail.refunded_at)})` : '')],
                ].map(([l, v]) => (
                  <div key={l} className="contents">
                    <p className="text-slate-500">{l}</p><p className="text-slate-900 font-medium break-all">{String(v)}</p>
                  </div>
                ))}
              </div>
              {detail.fiscal && (
                <div className="mt-4 bg-slate-50 rounded-xl p-4 text-sm">
                  <p className="text-xs uppercase tracking-wide text-slate-500 font-semibold mb-2">Dati fiscali cliente</p>
                  <p className="text-slate-700">
                    {detail.fiscal.company_name || `${detail.fiscal.first_name || ''} ${detail.fiscal.last_name || ''}`}
                    {detail.fiscal.partita_iva ? ` · P.IVA ${detail.fiscal.partita_iva}` : ''}
                    {detail.fiscal.codice_fiscale ? ` · CF ${detail.fiscal.codice_fiscale}` : ''}
                    {detail.fiscal.pec ? ` · PEC ${detail.fiscal.pec}` : ''}
                    {detail.fiscal.codice_destinatario ? ` · SDI ${detail.fiscal.codice_destinatario}` : ''}
                  </p>
                </div>
              )}
              <div className="mt-5">
                <Label>Stato documento fiscale</Label>
                <Select value={detail.document_status} onValueChange={(v) => setDocStatus(detail.order_id, v)}>
                  <SelectTrigger className="mt-1.5" data-testid="detail-doc-status"><SelectValue /></SelectTrigger>
                  <SelectContent>{DOC_STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
